import { Button, createTheme, Grid, IconButton, Theme, ThemeProvider } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import createBreakpoints from "@material-ui/core/styles/createBreakpoints";
import { Close, FilterList } from "@material-ui/icons";
import TextField from "components/TextField";
import { COLORS } from "constants/colors";
import useText from "constants/text";
import moment from "moment";
import React from "react";
import "styled-components/macro";
import Autocomplete from "ui/Autocomplete";
import Dialog from "ui/Dialog";
import { dateContext } from "./context/dateContext";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    btnFilter: {
      backgroundColor: "rgba(64, 180, 229, 0.25)",
      flexDirection: "row",
      height: "52px",
      "&:hover": {
        background: "rgba(64, 180, 229, 0.25)",
      },
      [theme.breakpoints.down("xs")]: {
        backgroundColor: "inherit",
        height: 40,
      },
    },
    btnReset: {
      flexDirection: "row",
      height: "52px",
      marginLeft: "10px",
      marginRight: "-21px",
      "&:hover": {
        background: "rgba(64, 180, 229, 0.25)",
      },
      [theme.breakpoints.down("xs")]: {
        display: "none",
      },
    },
    labelFilter: {
      color: COLORS.primary,
      fontFamily: "Montserrat",
      fontWeight: "bold",
      [theme.breakpoints.down("xs")]: {
        color: COLORS.gray,
      },
    },
    closeButton: {
      display: "none",
      [theme.breakpoints.down("xs")]: {
        display: "flex",
        marginLeft: "auto",
        marginTop: -29,
        marginRight: -41,
        padding: 0,
      },
    },
    input: {
      height: "1rem",
      color: "red",
      width: "100%",
    },
    statusLabel: {
      marginTop: "40px",
      [theme.breakpoints.down("xs")]: { marginTop: "6px" },
    },
    inputFrom: {
      marginTop: "20px",
      [theme.breakpoints.down("xs")]: { display: "grid", position: "relative", top: -20 },
    },
    labelTo: {
      marginTop: "40px",
      [theme.breakpoints.down("xs")]: { marginBottom: "18px" },
    },
  })
);
const breakpoints = createBreakpoints({});
const buttonIcon = createTheme({
  overrides: {
    MuiButton: {
      endIcon: {
        color: COLORS.primary,
        [breakpoints.down("xs")]: {
          display: "none",
        },
      },
    },
  },
});
export type DateQuery = {
  from?: string;
  to?: string;
};

const defaultDate = new Date();

const EmptyDateQuery: DateQuery = {
  from: null,
  to: moment(defaultDate).add(1, "days").format("YYYY-MM-DD"),
};
export interface DateFilterProps {
  type?: string;
  statusKey?: string;
  status: string[];
}

export function DateFilter({ status, statusKey }: DateFilterProps) {
  const styles = useStyles();
  const { text } = useText();
  const [open, setOpen] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const { selectedDate, setSelectedDate, setSelectedStatus, selectedStatus } = React.useContext(dateContext);
  const [date, setDate] = React.useState(EmptyDateQuery);
  const [isStatus, setIsStatus] = React.useState<string[]>([]);
  const [filter, setFilter] = React.useState(false);

  const onQueryConfirm = () => {
    setFilter(!filter);
  };

  const handleDateChange = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setDate((prev: any) => ({ ...prev, [name]: value }));
  };
  const openFilterDialog = () => {
    setOpen(true);
  };
  const closeFilter = () => {
    setSelectedDate(EmptyDateQuery);
    setSelectedStatus(null);
    setOpen(false);
    setFilter(false);
    setIsStatus([]);
    setIsSubmitted(false);
  };
  const handleReset = () => {
    setSelectedDate(EmptyDateQuery);
    setSelectedStatus(null);
    setFilter(false);
  };
  const handleCloseModal = () => {
    setOpen(false);
    setIsStatus([]);
    setIsSubmitted(false);
  };
  const handleConfirm = () => {
    setIsSubmitted(true);
  };
  React.useEffect(() => {
    if (isSubmitted) {
      setSelectedStatus(isStatus);
      setSelectedDate(date);
    }
    setTimeout(() => {
      handleCloseModal();
    }, 200);
  }, [isSubmitted]);

  React.useEffect(() => {
    setSelectedStatus([]);
    setSelectedDate(EmptyDateQuery);
  }, [statusKey]);

  React.useEffect(() => {
    if (selectedStatus?.length > 0 || date.from !== null) {
      onQueryConfirm();
    }
  }, [selectedStatus]);

  const handleField = (event: any, value: any) => setIsStatus([value]);

  return (
    <Grid container spacing={2} alignItems="center">
      <Grid item>
        <ThemeProvider theme={buttonIcon}>
          <Button
            className={styles.btnFilter}
            aria-label="more"
            aria-controls="customized-menu"
            aria-haspopup="true"
            onClick={openFilterDialog}
            variant="outlined"
            endIcon={<FilterList />}
          >
            <span className={styles.labelFilter}>{text.parcel.filtrer_text}</span>
          </Button>
        </ThemeProvider>
        {filter && (
          <>
            <Button
              className={styles.btnReset}
              aria-label="more"
              aria-controls="customized-menu"
              aria-haspopup="true"
              onClick={handleReset}
              variant="text"
            >
              <span className={styles.labelFilter}>{text.all.reset}</span>
            </Button>
            <IconButton onClick={handleReset} className={styles.closeButton}>
              <Close fontSize="small" color="action" />
            </IconButton>
          </>
        )}
        <Dialog
          open={open}
          title={text.all.filter_by_date}
          confirmButtonTitle={text.all.confirm}
          handleClose={closeFilter}
          cancelButtonTitle={text.all.cancel}
          handleConfirm={handleConfirm}
        >
          <Grid container spacing={2} style={{ marginTop: -21 }}>
            <Grid item xs={12} sm={12} className={styles.inputFrom}>
              <TextField
                label={text.parcel.placeholder_from}
                name="from"
                type="date"
                value={selectedDate.from}
                variant="outlined"
                className={styles.input}
                onChange={handleDateChange}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  max: selectedDate.to,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={12} className={styles.labelTo}>
              <TextField
                label={text.parcel.placeholder_to}
                name="to"
                type="date"
                className={styles.input}
                value={selectedDate.to}
                variant="outlined"
                onChange={handleDateChange}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: selectedDate.from,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={12} className={styles.statusLabel}>
              <Autocomplete
                getOptionSelected={(option: any, value: any) => option === value}
                fullWidth
                name="status"
                value={isStatus && isStatus[0]}
                className={styles.input}
                label={text.all.status}
                getOptionLabel={(option: any) => option.replace(/_/g, " ")}
                options={status}
                onChange={(event: any, value: any) => handleField(event, value)}
              />
            </Grid>
          </Grid>
        </Dialog>
      </Grid>
    </Grid>
  );
}
